export class OrgAuditTab extends HTMLElement {
  private _auditLogs: any[] = [];
  private _expandedRows: Set<string> = new Set();
  private _total: number = 0;
  private _offset: number = 0;
  private _limit: number = 20;
  private _filters: any = {
    user_id: '',
    target_id: '',
    action: '',
    from: '',
    to: ''
  };

  set auditLogs(val: any[]) {
    this._auditLogs = Array.isArray(val) ? val : [];
    this.render();
  }

  set total(val: number) {
    this._total = val;
    this.render();
  }

  set offset(val: number) {
    this._offset = val;
    this.render();
  }

  set filters(val: any) {
    this._filters = { ...this._filters, ...val };
    this.render();
  }

  connectedCallback() {
    this.render();
  }

  private toggleRow(id: string) {
    if (this._expandedRows.has(id)) {
      this._expandedRows.delete(id);
    } else {
      this._expandedRows.add(id);
    }
    this.render();
  }

  private render() {
    this.innerHTML = `
      <style>
        .audit-container {
          display: flex;
          flex-direction: column;
          gap: 20px;
          animation: fade-in 0.3s ease-out;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .audit-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 4px;
        }

        .audit-table {
          width: 100%;
          border-collapse: collapse;
          font-family: var(--font-main);
          font-size: 0.9rem;
        }

        .audit-table th {
          text-align: left;
          padding: 16px;
          color: var(--text-dim);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 600;
          border-bottom: 1px solid var(--border-soft, rgba(255,255,255,0.1));
        }

        .audit-row {
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .audit-row:hover {
          background-color: rgba(245, 158, 11, 0.04);
          background-color: var(--bg-surface-hover, rgba(255, 255, 255, 0.03));
        }

        .audit-row.expanded {
          background-color: rgba(245, 158, 11, 0.06);
        }

        .audit-table td {
          padding: 16px 12px;
          vertical-align: middle;
          font-size: 14px;
        }

        .audit-row:last-child td {
          border-bottom: none;
        }

        .action-text {
          font-weight: 500;
          text-transform: capitalize;
        }

        .target-text {
          color: var(--app-text-muted);
        }

        .audit-cell-timestamp {
          color: var(--text-dim);
          font-variant-numeric: tabular-nums;
          font-size: 0.85rem;
        }

        .audit-cell-actor {
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--text-dim);
          font-size: 0.85rem;
        }

        .details-row {
          background-color: rgba(0, 0, 0, 0.15);
        }

        .details-content {
          padding: 24px 24px 32px 48px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 32px;
          border-bottom: 1px solid var(--border-soft);
          animation: slide-down 0.2s ease-out;
        }

        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .detail-label {
          font-size: 0.65rem;
          text-transform: uppercase;
          color: var(--text-dim);
          letter-spacing: 0.1em;
          font-weight: 700;
        }

        .detail-value {
          font-size: 0.85rem;
          color: var(--text-main);
          font-family: var(--font-code, monospace);
          word-break: break-all;
          opacity: 0.9;
        }

        .metadata-json {
          grid-column: 1 / -1;
          background: rgba(0, 0, 0, 0.3);
          padding: 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          color: var(--accent);
          border: 1px solid var(--border-soft);
          max-height: 300px;
          overflow-y: auto;
          margin: 0;
        }

        .chevron {
          transition: transform 0.2s ease;
          color: var(--text-dim);
          opacity: 0.5;
        }

        .expanded .chevron {
          transform: rotate(90deg);
          color: var(--accent);
          opacity: 1;
        }

        .empty-state {
          text-align: center;
          padding: 80px 24px;
          color: var(--text-dim);
        }

        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-top: 1px solid var(--border-soft);
          background: rgba(0, 0, 0, 0.2);
        }

        .pagination-info {
          font-size: 0.85rem;
          color: var(--text-dim);
        }

        .pagination-controls {
          display: flex;
          gap: 8px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group label {
          font-size: 0.75rem;
          color: var(--text-dim);
          text-transform: uppercase;
          font-weight: 600;
        }

        .filter-group input {
          background: rgba(0,0,0,0.2);
          border: 1px solid var(--border-soft);
          color: var(--text-main);
          padding: 8px 12px;
          border-radius: 6px;
          font-family: var(--font-main);
          font-size: 0.85rem;
        }
        
        .filter-group input:focus {
          outline: none;
          border-color: var(--accent);
        }

        .active-filter-btn {
          color: var(--accent) !important;
          background: rgba(245, 158, 11, 0.1) !important;
        }

        /* Clean up badge styling for the table context */
        app-badge {
          --shadow-industrial: none; /* Attempt to override if possible, otherwise rely on component logic */
          flex-shrink: 0;
        }
      </style>

      <div class="audit-container">
        <div class="audit-header" style="justify-content: flex-end; margin-bottom: 16px;">
          <div style="display: flex; gap: 12px;">
            <app-button variant="ghost" size="sm" icon="download" id="export-audit-btn">Export CSV</app-button>
          </div>
        </div>

        <div class="filters-container sketchy-card" style="margin-bottom: 24px; padding: 20px; background: rgba(0,0,0,0.15);">
          <div style="position: relative; z-index: 2; display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end;">
            <div class="filter-group" style="flex: 1; min-width: 150px;">
                <label>User ID</label>
                <input type="text" name="user_id" placeholder="UUID..." value="${this._filters.user_id || ''}">
            </div>
            <div class="filter-group" style="flex: 1; min-width: 150px;">
                <label>Target ID</label>
                <input type="text" name="target_id" placeholder="UUID..." value="${this._filters.target_id || ''}">
            </div>
            <div class="filter-group" style="flex: 1; min-width: 150px;">
                <label>Action</label>
                <input type="text" name="action" placeholder="e.g. create.rule" value="${this._filters.action || ''}">
            </div>
            <div class="filter-group" style="flex: 1; min-width: 130px;">
                <label>From Date</label>
                <input type="date" name="from" value="${this._filters.from || ''}">
            </div>
            <div class="filter-group" style="flex: 1; min-width: 130px;">
                <label>To Date</label>
                <input type="date" name="to" value="${this._filters.to || ''}">
            </div>
            <div class="filter-group" style="flex: 0 0 auto;">
                <app-button variant="primary" size="sm" id="apply-filters-btn" icon="search">Search</app-button>
            </div>
          </div>
        </div>
        
        <div class="sketchy-card" style="padding: 0; overflow: hidden;">
          <div style="position: relative; z-index: 2;">
            <table class="audit-table">
              <thead>
                <tr>
                  <th style="width: 32px;"></th>
                  <th style="width: 180px;">Action</th>
                  <th>Target</th>
                  <th>Actor</th>
                  <th style="text-align: right;">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                ${this._auditLogs.map(log => {
                  const isExpanded = this._expandedRows.has(log.id);
                  const date = new Date(log.timestamp);
                  
                  return `
                    <tr class="audit-row ${isExpanded ? 'expanded' : ''}" data-id="${log.id}">
                      <td style="width: 32px; padding-right: 0;">
                        <app-icon name="chevron-right" size="sm" class="chevron"></app-icon>
                      </td>
                      <td>
                        <span class="action-text" style="color: ${this.getActionColor(log.action)};">${log.action}</span>
                      </td>
                      <td>
                        <span class="target-text">${log.target_name || log.target_type || 'N/A'}</span>
                      </td>
                      <td>
                        <div class="audit-cell-actor" data-tooltip="${log.actor_email}">${log.actor_email}</div>
                      </td>
                      <td style="text-align: right;">
                        <div class="audit-cell-timestamp">${this.formatRelativeTime(date)}</div>
                      </td>
                    </tr>
                    ${isExpanded ? `
                      <tr class="details-row">
                        <td colspan="5">
                          <div class="details-content">
                            <div class="detail-item">
                              <span class="detail-label">Full Timestamp</span>
                              <span class="detail-value">${date.toUTCString()}</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">IP Address</span>
                              <span class="detail-value">${log.ip_address || 'Unknown'}</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">User Agent</span>
                              <span class="detail-value">${log.user_agent || 'N/A'}</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">Log ID</span>
                              <span class="detail-value">${log.id}</span>
                            </div>
                            ${log.metadata && Object.keys(log.metadata).length > 0 ? `
                              <div class="detail-item" style="grid-column: 1 / -1;">
                                <span class="detail-label">Metadata</span>
                                <pre class="metadata-json">${JSON.stringify(log.metadata, null, 2)}</pre>
                              </div>
                            ` : ''}
                          </div>
                        </td>
                      </tr>
                    ` : ''}
                  `;
                }).join('')}
              </tbody>
            </table>
            ${this._auditLogs.length === 0 ? `
              <div class="empty-state">
                <app-icon name="search" size="xl" style="opacity: 0.2; margin-bottom: 16px;"></app-icon>
                <div style="font-family: var(--font-sketch); font-size: 1.2rem;">No activity logs recorded yet.</div>
                <div style="font-size: 0.9rem; margin-top: 8px;">Activity within your organization will appear here.</div>
              </div>
            ` : ''}
            
            ${this._total > this._limit ? `
              <div class="pagination">
                <div class="pagination-info">
                  Showing ${this._offset + 1} to ${Math.min(this._offset + this._limit, this._total)} of ${this._total} entries
                </div>
                <div class="pagination-controls">
                  <app-button variant="ghost" size="sm" id="prev-page-btn" ${this._offset === 0 ? 'disabled' : ''}>Previous</app-button>
                  <app-button variant="ghost" size="sm" id="next-page-btn" ${this._offset + this._limit >= this._total ? 'disabled' : ''}>Next</app-button>
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;

    // Add event listeners
    this.querySelectorAll('.audit-row').forEach(row => {
      row.addEventListener('click', () => {
        const id = row.getAttribute('data-id');
        if (id) this.toggleRow(id);
      });
    });

    const exportBtn = this.querySelector('#export-audit-btn');
    if (exportBtn) {
      exportBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('export-audit', { bubbles: true, composed: true }));
      });
    }

    const prevBtn = this.querySelector('#prev-page-btn');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (this._offset >= this._limit) {
          this.dispatchEvent(new CustomEvent('page-change', { 
            detail: { offset: this._offset - this._limit },
            bubbles: true, 
            composed: true 
          }));
        }
      });
    }

    const applyFiltersBtn = this.querySelector('#apply-filters-btn');
    if (applyFiltersBtn) {
      applyFiltersBtn.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('filter-change', {
          detail: { filters: { ...this._filters } },
          bubbles: true,
          composed: true
        }));
      });
    }

    this.querySelectorAll('.filter-group input').forEach(input => {
      input.addEventListener('change', (e) => {
        e.stopPropagation();
      });

      input.addEventListener('input', (e) => {
        e.stopPropagation();
        const target = e.target as HTMLInputElement;
        if (target.name in this._filters) {
          this._filters[target.name] = target.value;
        }
      });
      
      input.addEventListener('keydown', (e) => {
        e.stopPropagation();
        const keyboardEvent = e as KeyboardEvent;
        if (keyboardEvent.key === 'Enter') {
            this.dispatchEvent(new CustomEvent('filter-change', {
              detail: { filters: { ...this._filters } },
              bubbles: true,
              composed: true
            }));
        }
      });
    });

    const nextBtn = this.querySelector('#next-page-btn');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (this._offset + this._limit < this._total) {
          this.dispatchEvent(new CustomEvent('page-change', { 
            detail: { offset: this._offset + this._limit },
            bubbles: true, 
            composed: true 
          }));
        }
      });
    }
  }

  private getActionColor(action: string): string {
    const a = action.toLowerCase();
    if (a.includes('create') || a.includes('stored') || a.includes('add')) return 'var(--app-success)';
    if (a.includes('delete') || a.includes('remove') || a.includes('revoke')) return 'var(--app-error)';
    if (a.includes('update') || a.includes('edit')) return 'var(--app-warning)';
    return 'var(--app-primary)';
  }

  private formatRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString();
  }
}

if (!customElements.get('org-audit-tab')) {
  customElements.define('org-audit-tab', OrgAuditTab);
}
